import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { USE_STATIC_SITE_CONTENT } from '../config/staticSiteMode';
import { STATIC_HABER_ITEMS } from '../data/staticSite/content';
import { mapNewsFallbackFromI18n } from '../data/newsItems';

import 'swiper/css';
import 'swiper/css/pagination';

/**
 * @param {{
 *   item: {
 *     id: string,
 *     imageUrl: string,
 *     publishedAt: string,
 *     title: string,
 *     excerpt: string,
 *     href: string,
 *   },
 * }} props
 */
export function HaberNewsCard({ item }) {
    const { t, i18n } = useTranslation();
    const dateLocale = i18n.language?.toLowerCase().startsWith('en')
        ? 'en-US'
        : 'tr-TR';

    function formatNewsDate(isoDate) {
        const d = new Date(isoDate);
        if (Number.isNaN(d.getTime())) return isoDate;
        return d.toLocaleDateString(dateLocale, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        });
    }

    return (
        <article className="haber-card w-100 h-100 d-flex flex-column rounded-4 overflow-hidden">
            <div className="haber-card-image-wrap position-relative">
                <div className="haber-card-image ratio ratio-16x9">
                    <img
                        src={item.imageUrl}
                        alt={item.title}
                        className="object-fit-cover w-100 h-100"
                        loading="lazy"
                    />
                </div>
                <time
                    className="haber-card-date position-absolute rounded-pill px-3 py-2 small fw-semibold"
                    dateTime={item.publishedAt}
                >
                    {formatNewsDate(item.publishedAt)}
                </time>
            </div>
            <div className="haber-card-body d-flex flex-column flex-grow-1 px-4 pt-4 pb-4">
                <h3 className="haber-card-title text-white h5 mb-3">
                    {item.title}
                </h3>
                <p className="haber-card-excerpt small mb-4 flex-grow-1">
                    {item.excerpt}
                </p>
                <hr className="haber-card-divider my-0 mb-3 opacity-100" />
                <Link
                    to={item.href}
                    className="haber-card-link d-inline-flex align-items-center justify-content-center gap-2 text-white text-decoration-none small fw-semibold"
                >
                    {t('home.news.readMore')}
                    <ArrowRight size={18} strokeWidth={2} aria-hidden />
                </Link>
            </div>
        </article>
    );
}

/** @param {{ count?: number, statusLabel: string }} props */
function HaberlerLoadingSkeleton({ count = 3, statusLabel }) {
    return (
        <div role="status" aria-live="polite" aria-busy="true">
            <span className="visually-hidden">{statusLabel}</span>
            <SkeletonTheme baseColor="#e8ecf2" highlightColor="#f0f3f8">
                <ul className="haberler-grid list-unstyled row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 mb-0">
                    {Array.from({ length: count }, (_, i) => (
                        <li
                            key={`haber-sk-${i}`}
                            className="col d-flex"
                            aria-hidden
                        >
                            <article className="haber-card haber-card--skeleton w-100 h-100 d-flex flex-column rounded-4 overflow-hidden">
                                <div className="haber-card-image-wrap position-relative">
                                    <div className="haber-card-image ratio ratio-16x9">
                                        <Skeleton
                                            className="haber-card-skeleton-media"
                                            height="100%"
                                            borderRadius={0}
                                        />
                                    </div>
                                    <Skeleton
                                        height={34}
                                        width={100}
                                        borderRadius={999}
                                        className="haber-card-skeleton-date"
                                    />
                                </div>
                                <div className="haber-card-body d-flex flex-column flex-grow-1 px-4 pt-4 pb-4">
                                    <Skeleton
                                        height={22}
                                        width="92%"
                                        className="mb-3"
                                        borderRadius={8}
                                    />
                                    <Skeleton
                                        height={13}
                                        className="mb-2"
                                        borderRadius={6}
                                    />
                                    <Skeleton
                                        height={13}
                                        className="mb-2"
                                        borderRadius={6}
                                    />
                                    <Skeleton
                                        height={13}
                                        width="75%"
                                        className="mb-4"
                                        borderRadius={6}
                                    />
                                    <hr className="haber-card-divider my-0 mb-3 opacity-100" />
                                    <Skeleton
                                        height={40}
                                        width={160}
                                        borderRadius={999}
                                    />
                                </div>
                            </article>
                        </li>
                    ))}
                </ul>
            </SkeletonTheme>
        </div>
    );
}

/** @param {{ items: Array<{ id: string, imageUrl: string, publishedAt: string, title: string, excerpt: string, href: string }> }} props */
function HaberlerCarousel({ items }) {
    const { t } = useTranslation();
    const swiperRef = useRef(null);
    const enableLoop = items.length > 3;
    const [nav, setNav] = useState({ prev: false, next: true });

    const updateNav = useCallback(
        (swiper) => {
            if (!swiper) return;
            if (swiper.isLocked) {
                setNav({ prev: false, next: false });
                return;
            }
            if (enableLoop) {
                setNav({ prev: true, next: true });
                return;
            }
            setNav({
                prev: !swiper.isBeginning,
                next: !swiper.isEnd,
            });
        },
        [enableLoop]
    );

    return (
        <div className="position-relative card-swiper-shell px-xl-4">
            <button
                type="button"
                className="card-swiper-nav card-swiper-nav-prev"
                disabled={!nav.prev}
                aria-label={t('home.news.prevNav')}
                onClick={() => swiperRef.current?.slidePrev()}
            >
                <ChevronLeft size={28} strokeWidth={1.65} />
            </button>
            <button
                type="button"
                className="card-swiper-nav card-swiper-nav-next"
                disabled={!nav.next}
                aria-label={t('home.news.nextNav')}
                onClick={() => swiperRef.current?.slideNext()}
            >
                <ChevronRight size={28} strokeWidth={1.65} />
            </button>
            <Swiper
                className="haberler-swiper pb-5"
                modules={[Pagination]}
                slidesPerView={1}
                spaceBetween={16}
                loop={enableLoop}
                pagination={{ clickable: true }}
                watchOverflow
                onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                    updateNav(swiper);
                }}
                onSlideChange={updateNav}
                onBreakpoint={(swiper) => updateNav(swiper)}
                breakpoints={{
                    768: {
                        slidesPerView: 2,
                        spaceBetween: 20,
                    },
                    992: {
                        slidesPerView: 3,
                        spaceBetween: 24,
                    },
                }}
            >
                {items.map((item) => (
                    <SwiperSlide key={item.id}>
                        <HaberNewsCard item={item} />
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );
}

/**
 * @param {{ items: Array<{ id: string, imageUrl: string, publishedAt: string, title: string, excerpt: string, href: string }> }} props
 */
export function HaberlerGrid({ items }) {
    return (
        <ul className="haberler-grid list-unstyled row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 mb-0">
            {items.map((item) => (
                <li key={item.id} className="col d-flex">
                    <HaberNewsCard item={item} />
                </li>
            ))}
        </ul>
    );
}

/**
 * @param {{
 *   items?: Array<{ id: string, imageUrl: string, publishedAt: string, title: string, excerpt: string, href: string }> | null,
 *   maxItems?: number,
 *   showSeeAllLink?: boolean,
 *   innerContainer?: boolean,
 *   showSectionTitle?: boolean,
 *   variant?: 'carousel' | 'grid',
 *   excludeNewsIds?: string[],
 * }} props
 * `items` verilmezse CMS’den yüklenir. `maxItems` ile (ör. ana sayfa) sınır.
 */
export function Haberler({
    items: itemsProp,
    maxItems,
    showSeeAllLink = true,
    innerContainer = true,
    showSectionTitle = true,
    variant = 'carousel',
    excludeNewsIds = [],
}) {
    const { t } = useTranslation();
    const [items, setItems] = useState(
        () => (itemsProp !== undefined && itemsProp !== null ? itemsProp : null),
    );

    const fallbackItems = useMemo(() => {
        const raw = t('newsList.fallbackItems', { returnObjects: true });
        return mapNewsFallbackFromI18n(raw);
    }, [t]);

    const excludedIdSet = useMemo(
        () => new Set(excludeNewsIds.filter(Boolean)),
        [excludeNewsIds],
    );

    const applyListLimits = useCallback(
        (list) => {
            const withoutExcluded = excludedIdSet.size
                ? list.filter((x) => !excludedIdSet.has(x.id))
                : list;
            return maxItems != null
                ? withoutExcluded.slice(0, maxItems)
                : withoutExcluded;
        },
        [excludedIdSet, maxItems],
    );

    useEffect(() => {
        if (itemsProp !== undefined && itemsProp !== null) {
            setItems(applyListLimits(itemsProp));
            return;
        }

        if (USE_STATIC_SITE_CONTENT) {
            setItems(applyListLimits(STATIC_HABER_ITEMS));
            return undefined;
        }

        let cancelled = false;

        /*
        (async () => {
            try {
                const raw = await fetchCmsNewsList({
                    pageNumber: 1,
                    pageSize: 50,
                });
                const mapped = raw
                    .map(mapCmsNewsToHaberItem)
                    .filter((x) => x.imageUrl);
                if (cancelled) return;
                setItems(applyListLimits(mapped));
            } catch {
                if (cancelled) return;
                setItems(applyListLimits(fallbackItems));
            }
        })();
        */

        return () => {
            cancelled = true;
        };
    }, [itemsProp, maxItems, fallbackItems, applyListLimits]);

    const content = (
        <>
            {showSectionTitle ? (
                <h2
                    id="haberler-baslik"
                    className="haberler-section-title text-center mb-4 mb-lg-5"
                >
                    {t('home.news.title')}
                </h2>
            ) : null}
            {items == null ? (
                <HaberlerLoadingSkeleton
                    count={variant === 'grid' ? 9 : 3}
                    statusLabel={t('home.news.loading')}
                />
            ) : items.length === 0 ? (
                <p className="text-center text-secondary mb-0 py-5">
                    {t('home.news.empty')}
                </p>
            ) : variant === 'grid' ? (
                <HaberlerGrid items={items} />
            ) : (
                <HaberlerCarousel items={items} />
            )}
            {showSeeAllLink ? (
                <div className="text-center mt-4 pt-1">
                    <Link
                        to="/news"
                        className="d-inline-block rounded-pill section-see-all-btn text-decoration-none"
                    >
                        {t('home.news.seeAll')}
                    </Link>
                </div>
            ) : null}
        </>
    );

    return (
        <section
            className={`haberler-section bg-light${variant === 'grid' ? ' haberler-section--grid' : ''}`}
            aria-labelledby={
                showSectionTitle ? 'haberler-baslik' : undefined
            }
            aria-label={
                showSectionTitle ? undefined : t('home.news.sectionAria')
            }
            aria-busy={items == null ? 'true' : undefined}
        >
            {innerContainer ? (
                <div className="container">{content}</div>
            ) : (
                content
            )}
        </section>
    );
}
